import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import { useHistory } from "react-router-dom";
import {
  type Case,
  type Client,
  DocumentStatus,
  DocumentType,
  Source,
} from "@/briefpoint-client";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { SUPPLEMENTAL_FEATURES_FF } from "@/hooks/useConfig";
import type { DocumentClient } from "@/lib/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import AddToCalendarModal from "./AddToCalendarModal";

export default function DocumentActionsMenu({
  document,
  _case,
  client,
  archiveDocument,
  editDueDate,
  setShowSetCase,
  downloadForm,
  downloadRequest,
  createReview,
  sendReminder,
}: {
  document: DocumentClient;
  _case?: Case;
  client?: Client | undefined;
  archiveDocument: (document: DocumentClient) => Promise<void>;
  editDueDate?: Dispatch<SetStateAction<DocumentClient | undefined>>;
  setShowSetCase?: Dispatch<SetStateAction<DocumentClient | undefined>>;
  downloadForm: (
    document: DocumentClient,
    formId: string,
    formTypeName: string,
  ) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;
  createReview?: (document: DocumentClient) => void;
  sendReminder?: () => void;
}) {
  const [showAddToCalendar, setShowAddToCalendar] = useState(false);
  const { jurisdictions, user, firm } = useAuth();
  const history = useHistory();
  const [, featureFlags] = useConfig();

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !_case?.jurisdiction) {
      return undefined;
    }

    const jx = jurisdictions?.find((j) => j.id === _case?.jurisdiction);
    return jx;
  }, [jurisdictions, _case?.jurisdiction]);

  const dueDate = document?.info?.dueDateOverridden || document?.info?.dueDate;
  const verificationFormId = jurisdiction?.verificationFormId;
  const isExternal = document.source !== Source.InternalGenerated;

  const useSupplementalFeatures = featureFlags()[SUPPLEMENTAL_FEATURES_FF]
    ? featureFlags()[SUPPLEMENTAL_FEATURES_FF](user, firm)
    : false;
  const showSupplementOption =
    useSupplementalFeatures &&
    document.info?.documentType === DocumentType.DiscoveryRequest &&
    document.source === Source.ExternalUploaded;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ThreeDotsVertical size={14} color="var(--text)" />
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent>
            {isExternal ? (
              <>
                {editDueDate &&
                  (document.status === DocumentStatus.Complete ||
                    document.status === DocumentStatus.InfoConfirmed) && (
                    <DropdownMenuItem onClick={() => editDueDate(document)}>
                      {`${dueDate ? "Edit" : "Set"} Due Date`}
                    </DropdownMenuItem>
                  )}
                {dueDate && (
                  <DropdownMenuItem onClick={() => setShowAddToCalendar(true)}>
                    Add to Calendar
                  </DropdownMenuItem>
                )}
                {setShowSetCase && !document.caseId && (
                  <DropdownMenuItem onClick={() => setShowSetCase(document)}>
                    Set Case & Client
                  </DropdownMenuItem>
                )}
                {createReview && (
                  <DropdownMenuItem onClick={() => createReview(document)}>
                    Send to Client
                  </DropdownMenuItem>
                )}
                {sendReminder && (
                  <DropdownMenuItem onClick={() => sendReminder()}>
                    Send Client a Reminder
                  </DropdownMenuItem>
                )}
                {!!document.caseId &&
                  (document.status ?? 0) > DocumentStatus.Processed &&
                  verificationFormId && (
                    <DropdownMenuItem
                      onClick={() =>
                        downloadForm(
                          document,
                          verificationFormId,
                          "Verification",
                        )
                      }
                    >
                      Download Verification
                    </DropdownMenuItem>
                  )}
                {showSupplementOption && (
                  <DropdownMenuItem
                    onClick={() =>
                      history.push(
                        `/documents/${document.id}/supplemental-response`,
                      )
                    }
                  >
                    Supplement
                  </DropdownMenuItem>
                )}
              </>
            ) : (
              <DropdownMenuItem onClick={() => downloadRequest(document)}>
                Download
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => archiveDocument(document)}>
              Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>

      {dueDate && (
        <AddToCalendarModal
          isShow={showAddToCalendar}
          doc={document}
          client={client}
          _case={_case}
          onClose={() => setShowAddToCalendar(false)}
        />
      )}
    </>
  );
}
